import React, { useState, useEffect, useRef } from 'react';
import logo from '../Images/logo-main-black.png';
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faHome, faCommentSms, faCalendarDay, 
    faChartPie, faVideo, faUsers, faChevronDown, faChevronUp, 
    faCode, faChalkboardTeacher, faBuilding, faUserTie, faFileInvoice, 
    faLaptopCode, faUserFriends, faEnvelope, faSitemap, faBriefcase, faHandshake, faCompass,
    faMoneyBill, faNewspaper, faPercent
} from '@fortawesome/free-solid-svg-icons';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { addDoc, collection, getDocs, onSnapshot, orderBy, query, serverTimestamp, where } from "firebase/firestore";
import { db } from '../firebase';
import { recordAdminActivity } from '../utils/adminPresence';
import { normalizeAdminType, resolveAdminRecord } from '../utils/adminAccess';
import {
    isPaidAdmissionFeeButNotJoined
} from '../utils/admissions';
import { useShellContext } from './ShellContext';

const LEFTBAR_COLLAPSE_KEY = 'leftbarCollapseState';
const LEFTBAR_SCROLL_KEY = 'leftbarScrollTop';
const LEFTBAR_EMAIL_BADGE_KEY = 'leftbarEmailBadgeState';
const LEFTBAR_EMAIL_BADGE_EVENT = 'leftbar-email-badge-update';
const LEFTBAR_SMS_BALANCE_KEY = 'leftbarSmsBalanceState';
const LEFTBAR_SMS_BALANCE_EVENT = 'leftbar-sms-balance-update';
const MAIL_API_BASE_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/mailApi';
const SMS_BALANCE_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/smsBalanceCheck/sms-balance';
const EMAIL_BADGE_POLL_INTERVAL_MS = 5 * 60 * 1000;
const SMS_BALANCE_POLL_INTERVAL_MS = 5 * 60 * 1000;

const readStoredScrollTop = () => {
    if (typeof window === 'undefined') {
        return 0;
    }

    const rawValue = window.sessionStorage.getItem(LEFTBAR_SCROLL_KEY);
    const parsedValue = Number(rawValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const readStoredEmailBadgeState = () => {
    if (typeof window === 'undefined') {
        return {
            count: 0,
            newestUnreadId: '',
            hasFreshUnread: false,
            checkedAt: 0
        };
    }

    try {
        const rawState = window.localStorage.getItem(LEFTBAR_EMAIL_BADGE_KEY);
        if (!rawState) {
            return {
                count: 0,
                newestUnreadId: '',
                hasFreshUnread: false,
                checkedAt: 0
            };
        }

        const parsedState = JSON.parse(rawState);
        return {
            count: Number(parsedState?.count) || 0,
            newestUnreadId: String(parsedState?.newestUnreadId || ''),
            hasFreshUnread: Boolean(parsedState?.hasFreshUnread),
            checkedAt: Number(parsedState?.checkedAt) || 0
        };
    } catch (error) {
        console.error('Failed to read email badge state:', error);
        return {
            count: 0,
            newestUnreadId: '',
            hasFreshUnread: false,
            checkedAt: 0
        };
    }
};

const getSmsBalanceLevel = (credits) => {
    const numericCredits = Number(credits);

    if (!Number.isFinite(numericCredits)) {
        return 'unknown';
    }

    if (numericCredits < 50) {
        return 'critical';
    }

    if (numericCredits < 200) {
        return 'warning';
    }

    return 'healthy';
};

const readStoredSmsBalanceState = () => {
    if (typeof window === 'undefined') {
        return {
            credits: null,
            clientName: '',
            checkedAt: 0,
            level: 'unknown'
        };
    }

    try {
        const rawState = window.localStorage.getItem(LEFTBAR_SMS_BALANCE_KEY);
        if (!rawState) {
            return {
                credits: null,
                clientName: '',
                checkedAt: 0,
                level: 'unknown'
            };
        }

        const parsedState = JSON.parse(rawState);
        const credits = Number(parsedState?.credits);

        return {
            credits: Number.isFinite(credits) ? credits : null,
            clientName: String(parsedState?.clientName || ''),
            checkedAt: Number(parsedState?.checkedAt) || new Date(parsedState?.checkedAt || 0).getTime() || 0,
            level: getSmsBalanceLevel(credits)
        };
    } catch (error) {
        console.error('Failed to read SMS balance state:', error);
        return {
            credits: null,
            clientName: '',
            checkedAt: 0,
            level: 'unknown'
        };
    }
};

const routeLabels = {
    "/dashboard": "Opened Dashboard",
    "/applications": "Opened Applications",
    "/applications-awaiting-join": "Opened Paid Fee, Not Joined",
    "/interviews": "Opened Admission Interviews",
    "/invoicing": "Opened Invoicing",
    "/active-students": "Opened Active Students",
    "/secondary-courses": "Opened Secondary Courses",
    "/learning-breaks": "Opened Learning Breaks",
    "/graduated-students": "Opened Graduated Students",
    "/students-graduating": "Opened Students Graduating",
    "/never-joined": "Opened Never Joined",
    "/other-students": "Opened Other Students",
    "/active-students-with-cleared-fees": "Opened Active Students with Cleared Fees",
    "/active-students-with-uncleared-fees": "Opened Active Students with Uncleared Fees",
    "/active-students-paying-fees": "Opened Active Students Paying Fees",
    "/active-students-not-paying-fees": "Opened Active Students Not Paying Fees",
    "/fee-collection-standing": "Opened Fee Collection Standing",
    "/active-attendance-students": "Opened Active Attendance Students",
    "/inactive-attendance-students": "Opened Inactive Attendance Students",
    "/daily-attendance-register": "Opened Daily Attendance Register",
    "/android-app-activity": "Opened Android App Activity",
    "/intake-success-analysis": "Opened Intake Success Analysis",
    "/sources-of-students": "Opened Sources of Students",
    "/conversion-by-source": "Opened Conversion by Source",
    "/trends-over-time": "Opened Trends Over Time",
    "/course-by-source": "Opened Course by Source",
    "/demographics-by-source": "Opened Demographics By Source",
    "/campus-type": "Opened Campus Type by Source",
    "/drop-off-analysis": "Opened Drop-off Analysis",
    "/visitor-journey": "Opened Visitor Journey",
    "/application-to-adm-time": "Opened Application to Admission Time",
    "/session-time-demand": "Opened Session Time Demand",
    "/distribution-by-country": "Opened Distribution By Country",
    "/country-analysis": "Opened Nationality vs Course Choice",
    "/courses": "Opened Courses",
    "/intakes": "Opened Intakes",
    "/darasa": "Opened Classes",
    "/branches": "Opened Campuses",
    "/lab-onboarding": "Opened Lab Onboarding",
    "/managed-products": "Opened Managed Product",
    "/managed-service": "Opened Managed Service",
    "/extended-teams": "Opened Extended Team",
    "/devs": "Opened Devs",
    "/lab-emails": "Opened Lab Emails",
    "/income": "Opened School Fee Income",
    "/admission-fees": "Opened Admission Fees",
    "/payment-audit": "Opened Payment Audit",
    "/other-income": "Opened Other Income",
    "/sent-invoices": "Opened Invoices",
    "/direct-expenses": "Opened Direct Expenses",
    "/projected-income": "Opened Projected Income",
    "/balance-sheet": "Opened Balance Sheet",
    "/fee-balances": "Opened Fee Balances",
    "/vendors": "Opened Vendors",
    "/fixed-bills": "Opened Fixed Bills",
    "/pay-bills": "Opened Pay Bills",
    "/requisitions": "Opened Requisitions",
    "/requisition-approvals": "Opened Requisition Approvals",
    "/statutory-remittances": "Opened Statutory Remittances",
    "/debts-loans": "Opened Liabilities",
    "/finance-reports": "Opened Finance Reports",
    "/employees": "Opened Employees",
    "/leave-request": "Opened Leave Request",
    "/payroll-ledger": "Opened Payroll Ledger",
    "/pay-salaries": "Opened Pay Salaries",
    "/cash-salary-payments": "Opened Cash Salary Payments",
    "/directors": "Opened Directors",
    "/shareholders": "Opened Shareholders",
    "/assets": "Opened Assets",
    "/sms": "Opened SMS",
    "/emails": "Opened Emails",
    "/fee-reminders": "Opened Fee Reminders",
    "/media-consent": "Opened Media Consent",
    "/legal-docs": "Opened Legal Docs",
    "/document-generator": "Opened Document Generator",
    "/generated-documents": "Opened Generated Documents",
    "/generated-documents/": "Opened Generated Document",
    "/banking-details": "Opened Banking Details",
    "/mpesa-details": "Opened M-Pesa Details",
    "/exchange-rate-info": "Opened Exchange Rate Info",
    "/paybill-balance": "Opened Paybill Balance",
    "/strategic-roadmap": "Opened Strategic Roadmap",
    "/jobs-scout": "Opened Jobs Scout",
    "/growth-opportunity-scout": "Opened Growth Opportunity Scout",
    "/news-posts": "Opened News Posts",
    "/enrolled-partners": "Opened Enrolled Partners",
    "/marketing-budget": "Opened Marketing Budgets",
    "/marketing-expenses": "Opened Marketing Expenses",
    "/admins": "Opened Admin Management",
    "/activity-logs": "Opened Activity Logs",
    "/profile": "Opened Profile",
    "/help-support": "Opened Help & Support",
    "/settings": "Opened Settings"
};

const getRouteLabel = (pathname) => {
    if (routeLabels[pathname]) return routeLabels[pathname];
    if (pathname.startsWith('/generated-documents/')) return 'Opened Generated Document';
    if (pathname.startsWith('/view-lab-client/')) return 'Opened Lab Client';
    if (pathname.startsWith('/view-employee/')) return 'Opened Employee Management';
    if (pathname.startsWith('/view-vendor/')) return 'Opened Vendor Management';
    if (pathname.startsWith('/news-posts/')) return 'Opened News Post Editor';
    return null;
};

function LeftBar({ forceRender = false }) {
    const { isShellActive } = useShellContext();
    let navigate = useNavigate();
    const location = useLocation();
    const auth = getAuth();
    const lastLoggedRef = useRef('');
    const leftNavRef = useRef(null);
    const initialEmailBadgeState = readStoredEmailBadgeState();
    const initialSmsBalanceState = readStoredSmsBalanceState();
    const [currentUser, setCurrentUser] = useState(null);
    const [adminRole, setAdminRole] = useState('admin');
    const [unreadEmailCount, setUnreadEmailCount] = useState(initialEmailBadgeState.count);
    const [hasFreshUnread, setHasFreshUnread] = useState(initialEmailBadgeState.hasFreshUnread);
    const [smsBalanceState, setSmsBalanceState] = useState(initialSmsBalanceState);
    const [unopenedLabCount, setUnopenedLabCount] = useState(0);
    const [applicationsAwaitingJoin, setApplicationsAwaitingJoin] = useState([]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user || null);
            if (!user) {
                navigate("/login");
            }
        });

        return () => unsubscribe();
    }, [auth, navigate]);

    useEffect(() => {
        let cancelled = false;

        const fetchAdminRole = async () => {
            if (!currentUser) {
                setAdminRole('admin');
                return;
            }

            try {
                const adminRecord = await resolveAdminRecord(currentUser);
                if (!cancelled) {
                    setAdminRole(normalizeAdminType(adminRecord?.adminType));
                }
            } catch (error) {
                console.error('Failed to load admin role for left bar:', error);
                if (!cancelled) {
                    setAdminRole('admin');
                }
            }
        };

        fetchAdminRole();

        return () => {
            cancelled = true;
        };
    }, [currentUser]);

    const [isCollapsed1, setIsCollapsed1] = useState(false);
    const [isCollapsed2, setIsCollapsed2] = useState(true);
    const [isCollapsed3, setIsCollapsed3] = useState(true);
    const [isCollapsed5, setIsCollapsed5] = useState(true);
    const [isCollapsed6, setIsCollapsed6] = useState(true);
    const [isCollapsed7, setIsCollapsed7] = useState(true);

    const preserveLeftNavScroll = (updateFn) => {
        const scrollTop = leftNavRef.current?.scrollTop || 0;
        updateFn();

        window.requestAnimationFrame(() => {
            if (leftNavRef.current) {
                leftNavRef.current.scrollTop = scrollTop;
            }
        });
    };

    const toggleCollapse1 = () => preserveLeftNavScroll(() => {
        if (isCollapsed1) {
            setIsCollapsed1(false);
            setIsCollapsed2(true);
            setIsCollapsed3(true);
            setIsCollapsed5(true);
            setIsCollapsed6(true);
            setIsCollapsed7(true);
            return;
        }

        setIsCollapsed1(true);
    });

    const toggleCollapse2 = () => preserveLeftNavScroll(() => {
        if (isCollapsed2) {
            setIsCollapsed1(true);
            setIsCollapsed2(false);
            setIsCollapsed3(true);
            setIsCollapsed5(true);
            setIsCollapsed6(true);
            setIsCollapsed7(true);
            return;
        }

        setIsCollapsed2(true);
    });

    const toggleCollapse3 = () => preserveLeftNavScroll(() => {
        if (isCollapsed3) {
            setIsCollapsed1(true);
            setIsCollapsed2(true);
            setIsCollapsed3(false);
            setIsCollapsed5(true);
            setIsCollapsed6(true);
            setIsCollapsed7(true);
            return;
        }

        setIsCollapsed3(true);
    });

    const toggleCollapse5 = () => preserveLeftNavScroll(() => {
        if (isCollapsed5) {
            setIsCollapsed1(true);
            setIsCollapsed2(true);
            setIsCollapsed3(true);
            setIsCollapsed5(false);
            setIsCollapsed6(true);
            setIsCollapsed7(true);
            return;
        }

        setIsCollapsed5(true);
    });

    const toggleCollapse6 = () => preserveLeftNavScroll(() => {
        if (isCollapsed6) {
            setIsCollapsed1(true);
            setIsCollapsed2(true);
            setIsCollapsed3(true);
            setIsCollapsed5(true);
            setIsCollapsed6(false);
            setIsCollapsed7(true);
            return;
        }

        setIsCollapsed6(true);
    });

    const toggleCollapse7 = () => preserveLeftNavScroll(() => {
        if (isCollapsed7) {
            setIsCollapsed1(true);
            setIsCollapsed2(true);
            setIsCollapsed3(true);
            setIsCollapsed5(true);
            setIsCollapsed6(true);
            setIsCollapsed7(false);
            return;
        }

        setIsCollapsed7(true);
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        window.localStorage.setItem(LEFTBAR_COLLAPSE_KEY, JSON.stringify({
            isCollapsed1,
            isCollapsed2,
            isCollapsed3,
            isCollapsed5,
            isCollapsed6,
            isCollapsed7
        }));
    }, [isCollapsed1, isCollapsed2, isCollapsed3, isCollapsed5, isCollapsed6, isCollapsed7]);

    useEffect(() => {
        if (!leftNavRef.current) return;
        leftNavRef.current.scrollTop = readStoredScrollTop();
    }, []);

    const handleLeftNavScroll = () => {
        if (typeof window === 'undefined' || !leftNavRef.current) return;
        window.sessionStorage.setItem(LEFTBAR_SCROLL_KEY, String(leftNavRef.current.scrollTop));
    };

    useEffect(() => {
        if (!currentUser) return;
        const label = getRouteLabel(location.pathname);
        if (!label) return;
        if (lastLoggedRef.current === location.pathname) return;
        lastLoggedRef.current = location.pathname;

        const adminName = currentUser.displayName || currentUser.email || 'Admin';
        addDoc(collection(db, 'AdminActivity'), {
            adminId: currentUser.uid,
            adminName,
            action: 'Opened Page',
            details: label,
            path: location.pathname,
            createdAt: serverTimestamp()
        }).catch((error) => {
            console.error('Failed to log page open:', error);
        });
    }, [currentUser, location.pathname]);

    useEffect(() => {
        if (!currentUser) return;

        recordAdminActivity({
            userId: currentUser.uid,
            email: currentUser.email,
            path: location.pathname,
            label: getRouteLabel(location.pathname) || 'Active on platform',
            source: 'route',
            force: true
        }).catch((error) => {
            console.error('Failed to update admin last active:', error);
        });
    }, [currentUser, location.pathname]);

    useEffect(() => {
        const syncActivity = (source) => {
            if (!currentUser) return;

            recordAdminActivity({
                userId: currentUser.uid,
                email: currentUser.email,
                path: location.pathname,
                label: getRouteLabel(location.pathname) || 'Active on platform',
                source
            }).catch((error) => {
                console.error('Failed to refresh admin activity:', error);
            });
        };

        const handleFocus = () => syncActivity('focus');
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                syncActivity('visibility');
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [currentUser, location.pathname]);

    useEffect(() => {
        if (!currentUser) {
            setUnreadEmailCount(0);
            setHasFreshUnread(false);
            return undefined;
        }

        let cancelled = false;

        const applyBadgeState = (badgeState) => {
            if (cancelled || !badgeState) return;
            setUnreadEmailCount(Number(badgeState.count) || 0);
            setHasFreshUnread(Boolean(badgeState.hasFreshUnread));
        };

        const fetchUnreadEmailCount = async () => {
            try {
                const response = await fetch(`${MAIL_API_BASE_URL}/read-inbox?limit=50`);
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || data.message || `Failed with status ${response.status}`);
                }

                const unreadEmails = Array.isArray(data.emails)
                    ? data.emails.filter((email) => !email.seen)
                    : [];
                const unreadCount = unreadEmails.length;
                const newestUnreadId = String(
                    unreadEmails[0]?.messageId || unreadEmails[0]?.uid || ''
                );
                const previousBadgeState = readStoredEmailBadgeState();
                const isFreshUnread = location.pathname === '/emails'
                    ? false
                    : unreadCount > 0 && (
                        unreadCount > previousBadgeState.count ||
                        (newestUnreadId && newestUnreadId !== previousBadgeState.newestUnreadId)
                    );

                applyBadgeState({
                    count: unreadCount,
                    hasFreshUnread: isFreshUnread
                });

                if (typeof window !== 'undefined') {
                    window.localStorage.setItem(LEFTBAR_EMAIL_BADGE_KEY, JSON.stringify({
                        count: unreadCount,
                        newestUnreadId,
                        hasFreshUnread: isFreshUnread,
                        checkedAt: Date.now()
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch unread email count:', error);
            }
        };

        fetchUnreadEmailCount();

        const intervalId = window.setInterval(fetchUnreadEmailCount, EMAIL_BADGE_POLL_INTERVAL_MS);
        const handleFocusRefresh = () => fetchUnreadEmailCount();
        const handleVisibilityRefresh = () => {
            if (document.visibilityState === 'visible') {
                fetchUnreadEmailCount();
            }
        };
        const handleBadgeUpdate = (event) => {
            if (event?.detail) {
                applyBadgeState(event.detail);
                return;
            }

            applyBadgeState(readStoredEmailBadgeState());
        };

        window.addEventListener('focus', handleFocusRefresh);
        document.addEventListener('visibilitychange', handleVisibilityRefresh);
        window.addEventListener(LEFTBAR_EMAIL_BADGE_EVENT, handleBadgeUpdate);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            window.removeEventListener('focus', handleFocusRefresh);
            document.removeEventListener('visibilitychange', handleVisibilityRefresh);
            window.removeEventListener(LEFTBAR_EMAIL_BADGE_EVENT, handleBadgeUpdate);
        };
    }, [currentUser, location.pathname]);

    useEffect(() => {
        if (!currentUser) {
            setSmsBalanceState({
                credits: null,
                clientName: '',
                checkedAt: 0,
                level: 'unknown'
            });
            return undefined;
        }

        let cancelled = false;

        const applySmsBalanceState = (nextState) => {
            if (cancelled || !nextState) return;

            const credits = Number(nextState.credits);
            setSmsBalanceState({
                credits: Number.isFinite(credits) ? credits : null,
                clientName: String(nextState.clientName || ''),
                checkedAt: Number(nextState.checkedAt) || new Date(nextState.checkedAt || 0).getTime() || Date.now(),
                level: getSmsBalanceLevel(credits)
            });
        };

        const fetchSmsBalance = async () => {
            try {
                const response = await fetch(SMS_BALANCE_API_URL);
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || data.message || `Failed with status ${response.status}`);
                }

                const credits = Number(data?.data?.sms_credits);
                const nextState = {
                    credits: Number.isFinite(credits) ? credits : null,
                    clientName: data?.data?.client_name || 'UBUNIFU COLLEGE',
                    checkedAt: Date.now()
                };

                applySmsBalanceState(nextState);

                if (typeof window !== 'undefined') {
                    window.localStorage.setItem(LEFTBAR_SMS_BALANCE_KEY, JSON.stringify(nextState));
                }
            } catch (error) {
                console.error('Failed to fetch SMS balance in left bar:', error);
            }
        };

        fetchSmsBalance();

        const intervalId = window.setInterval(fetchSmsBalance, SMS_BALANCE_POLL_INTERVAL_MS);
        const handleFocusRefresh = () => fetchSmsBalance();
        const handleVisibilityRefresh = () => {
            if (document.visibilityState === 'visible') {
                fetchSmsBalance();
            }
        };
        const handleSmsBalanceUpdate = (event) => {
            if (event?.detail) {
                applySmsBalanceState(event.detail);
                return;
            }

            applySmsBalanceState(readStoredSmsBalanceState());
        };

        window.addEventListener('focus', handleFocusRefresh);
        document.addEventListener('visibilitychange', handleVisibilityRefresh);
        window.addEventListener(LEFTBAR_SMS_BALANCE_EVENT, handleSmsBalanceUpdate);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            window.removeEventListener('focus', handleFocusRefresh);
            document.removeEventListener('visibilitychange', handleVisibilityRefresh);
            window.removeEventListener(LEFTBAR_SMS_BALANCE_EVENT, handleSmsBalanceUpdate);
        };
    }, [currentUser]);

    useEffect(() => {
        if (location.pathname !== '/emails') {
            return;
        }

        setHasFreshUnread(false);

        if (typeof window !== 'undefined') {
            const previousBadgeState = readStoredEmailBadgeState();
            window.localStorage.setItem(LEFTBAR_EMAIL_BADGE_KEY, JSON.stringify({
                ...previousBadgeState,
                hasFreshUnread: false
            }));
        }
    }, [location.pathname]);

    useEffect(() => {
        if (!currentUser || adminRole !== 'superAdmin') {
            setUnopenedLabCount(0);
            return undefined;
        }

        const callsQuery = query(collection(db, 'scheduledCalls'), orderBy('timestamp', 'desc'));

        const unsubscribe = onSnapshot(callsQuery, (snapshot) => {
            let nextCount = 0;

            snapshot.forEach((docItem) => {
                const data = docItem.data();
                if (!data?.viewed) {
                    nextCount += 1;
                }
            });

            setUnopenedLabCount(nextCount);
        }, (error) => {
            console.error('Failed to load lab onboarding badge count:', error);
            setUnopenedLabCount(0);
        });

        return () => unsubscribe();
    }, [currentUser, adminRole]);

    useEffect(() => {
        if (!currentUser) {
            setApplicationsAwaitingJoin([]);
            return undefined;
        }

        let cancelled = false;

        const fetchAwaitingJoinStudents = async () => {
            try {
                const snapshot = await getDocs(query(collection(db, 'users'), where('admissionFeeStatus', '==', 'paid')));
                const awaitingJoinStudents = [];

                snapshot.forEach((docItem) => {
                    const data = { id: docItem.id, ...docItem.data() };
                    if (isPaidAdmissionFeeButNotJoined(data)) {
                        awaitingJoinStudents.push(data);
                    }
                });

                if (!cancelled) {
                    setApplicationsAwaitingJoin(awaitingJoinStudents);
                }
            } catch (error) {
                console.error('Failed to load paid admission fee applicants:', error);
                if (!cancelled) {
                    setApplicationsAwaitingJoin([]);
                }
            }
        };

        fetchAwaitingJoinStudents();

        return () => {
            cancelled = true;
        };
    }, [currentUser]);

    const isAdmin = adminRole === 'admin' || adminRole === 'superAdmin';
    const isSuperAdmin = adminRole === 'superAdmin';
    if (isShellActive && !forceRender) {
        return null;
    }

    return (
        <div className='leftNav' ref={leftNavRef} onScroll={handleLeftNavScroll}>
            <div className='logoContainer'>
                <img src={logo} alt="" className='dashnavLogo' />
            </div>
            <br />
            <p>HOME</p>
            <NavLink to="/dashboard" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faHome} className="laftNavIcon activelaftNavIcon" />
                Dashboard
            </NavLink>
            <NavLink to="/applications" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faFileInvoice} className="laftNavIcon" />
                Applications
            </NavLink>
            <NavLink to="/applications-awaiting-join" className='leftNavLinks leftLinkCont d-flex align-items-center applicationsAwaitingJoinLink'>
                <FontAwesomeIcon icon={faUserFriends} className="laftNavIcon" />
                Awaiting Join
                {applicationsAwaitingJoin.length > 0 && (
                    <span className="leftLinkNotif leftLinkNotif--applications">{applicationsAwaitingJoin.length}</span>
                )}
            </NavLink>
            <NavLink to="/interviews" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faVideo} className="laftNavIcon" />
                Interviews
            </NavLink>
            <NavLink to="/invoicing" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faFileInvoice} className="laftNavIcon" />
                Invoicing
            </NavLink>

            {/* Students */}
            <div className='nav-item '>
                <div className='d-flex align-items-center leftNavLinks leftLinkCont dropdownMyCont' onClick={toggleCollapse1} style={{ cursor: 'pointer' }}>
                    <div>
                        <FontAwesomeIcon icon={faUsers} className="laftNavIcon" />
                        <span>Students</span>
                    </div>
                    <FontAwesomeIcon icon={isCollapsed1 ? faChevronDown : faChevronUp} className="ml-auto leftLinkNotif2" />
                </div>
                {!isCollapsed1 && (
                    <div className='submenu'>
                        <NavLink to="/active-students" className='nav-link'>Active Students</NavLink>
                        <NavLink to="/secondary-courses" className='nav-link'>Secondary Courses</NavLink>
                        <NavLink to="/learning-breaks" className='nav-link'>Learning Breaks</NavLink>
                        <NavLink to="/graduated-students" className='nav-link'>Graduated Students</NavLink>
                        <NavLink to="/students-graduating" className='nav-link'>Students Graduating</NavLink>
                        <NavLink to="/never-joined" className='nav-link'>Never Joined</NavLink>
                        <NavLink to="/other-students" className='nav-link'>Other Students</NavLink>
                    </div>
                )}
            </div>

            {isSuperAdmin && (
                <div className='nav-item '>
                    <div className='d-flex align-items-center leftNavLinks leftLinkCont dropdownMyCont' onClick={toggleCollapse5} style={{ cursor: 'pointer' }}>
                        <div>
                            <FontAwesomeIcon icon={faChartPie} className="laftNavIcon" />
                            <span>Data Analytics</span>
                        </div>
                        <FontAwesomeIcon icon={isCollapsed5 ? faChevronDown : faChevronUp} className="ml-auto leftLinkNotif2" />
                    </div>
                    {!isCollapsed5 && (
                        <div className='submenu'>
                            <NavLink to="/active-students-with-cleared-fees" className='nav-link'>Active Students with Cleared Fees</NavLink>
                            <NavLink to="/active-students-with-uncleared-fees" className='nav-link'>Active Students with Uncleared Fees</NavLink>
                            <NavLink to="/active-students-paying-fees" className='nav-link'>Active Students Paying Fees</NavLink>
                            <NavLink to="/active-students-not-paying-fees" className='nav-link'>Active Students Not Paying Fees</NavLink>
                            <NavLink to="/fee-collection-standing" className='nav-link'>Fee Collection Standing</NavLink>
                            <NavLink to="/active-attendance-students" className='nav-link'>Active Attendance Students</NavLink>
                            <NavLink to="/inactive-attendance-students" className='nav-link'>Inactive Attendance Students</NavLink>
                            <NavLink to="/daily-attendance-register" className='nav-link'>Daily Attendance Register</NavLink>
                            <NavLink to="/android-app-activity" className='nav-link'>Android App Activity</NavLink>
                            <NavLink to="/intake-success-analysis" className='nav-link'>Intake Success Analysis</NavLink>
                            <NavLink to="/sources-of-students" className='nav-link'>Sources of Students</NavLink>
                            <NavLink to="/conversion-by-source" className='nav-link'>Conversion by Source</NavLink>
                            <NavLink to="/trends-over-time" className='nav-link'>Trends Over Time</NavLink>
                            <NavLink to="/course-by-source" className='nav-link'>Course by Source</NavLink>
                            <NavLink to="/demographics-by-source" className='nav-link'>Demographics by Source</NavLink>
                            <NavLink to="/campus-type" className='nav-link'>Campus Type by Source</NavLink>
                            <NavLink to="/drop-off-analysis" className='nav-link'>Drop-off Analysis</NavLink>
                            <NavLink to="/visitor-journey" className='nav-link'>Visitor Journey</NavLink>
                            <NavLink to="/application-to-adm-time" className='nav-link'>Application to Admission Time</NavLink>
                            <NavLink to="/session-time-demand" className='nav-link'>Session Time Demand</NavLink>
                            <NavLink to="/distribution-by-country" className='nav-link'>Distribution By Country</NavLink>
                            <NavLink to="/country-analysis" className='nav-link'>Nationality vs Course Choice</NavLink>
                            <NavLink to="/activity-logs" className='nav-link'>Activity Logs</NavLink>
                        </div>
                    )}
                </div>
            )}

            <p>COMMUNICATIONS</p>
            <NavLink to="/sms" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faCommentSms} className="laftNavIcon " />
                SMS
                {smsBalanceState.credits !== null && (
                    <span
                        className={`leftLinkNotif leftLinkNotif--sms leftLinkNotif--sms-${smsBalanceState.level}`}
                        title={`${
                            smsBalanceState.level === 'critical'
                                ? 'Critical'
                                : smsBalanceState.level === 'warning'
                                    ? 'Low'
                                    : 'Healthy'
                        } SMS balance: ${smsBalanceState.credits ?? 0} credits left`}
                    >
                        {smsBalanceState.credits ?? 0}
                    </span>
                )}
            </NavLink>
            <NavLink to="/emails" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faEnvelope} className="laftNavIcon " />
                Emails
                <span className={`leftLinkNotif ${hasFreshUnread ? 'leftLinkNotif--fresh' : ''}`}>
                    {unreadEmailCount > 99 ? '99+' : unreadEmailCount}
                </span>
            </NavLink>
            <NavLink to="/fee-reminders" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faCalendarDay} className="laftNavIcon " />
                Fee Reminders
            </NavLink>
            <NavLink to="/media-consent" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faVideo} className="laftNavIcon " />
                Media Consent
            </NavLink>

            <p>BUSINESS INFO</p>
            <div className='nav-item '>
                <div className='d-flex align-items-center leftNavLinks leftLinkCont dropdownMyCont' onClick={toggleCollapse6} style={{ cursor: 'pointer' }}>
                    <div>
                        <FontAwesomeIcon icon={faBriefcase} className="laftNavIcon" />
                        <span>Business Info</span>
                    </div>
                    <FontAwesomeIcon icon={isCollapsed6 ? faChevronDown : faChevronUp} className="ml-auto leftLinkNotif2" />
                </div>
                {!isCollapsed6 && (
                    <div className='submenu'>
                        <NavLink to="/document-generator" className='nav-link'>Document Generator</NavLink>
                        <NavLink to="/generated-documents" className='nav-link'>Generated Documents</NavLink>
                        <NavLink to="/legal-docs" className='nav-link'>Legal Docs</NavLink>
                        <NavLink to="/banking-details" className='nav-link'>Banking Details</NavLink>
                        <NavLink to="/mpesa-details" className='nav-link'>M-Pesa Details</NavLink>
                        <NavLink to="/exchange-rate-info" className='nav-link'>Exchange Rate Info</NavLink>
                        <NavLink to="/paybill-balance" className='nav-link'>Paybill Balance</NavLink>
                    </div>
                )}
            </div>

            <p>FINANCE & HR</p>
            <div className='nav-item '>
                <div className='d-flex align-items-center leftNavLinks leftLinkCont dropdownMyCont' onClick={toggleCollapse2} style={{ cursor: 'pointer' }}>
                    <div>
                        <FontAwesomeIcon icon={faMoneyBill} className="laftNavIcon" />
                        <span>Finance</span>
                    </div>
                    <FontAwesomeIcon icon={isCollapsed2 ? faChevronDown : faChevronUp} className="ml-auto leftLinkNotif2" />
                </div>
                {!isCollapsed2 && (
                    <div className='submenu'>
                        <NavLink to="/income" className='nav-link'>School fee Income</NavLink>
                        <NavLink to="/admission-fees" className='nav-link'>Admission Fees</NavLink>
                        <NavLink to="/student-discounts" className='nav-link'>
                            <FontAwesomeIcon icon={faPercent} className="laftNavIcon" />
                            Student Discounts
                        </NavLink>
                        <NavLink to="/payment-audit" className='nav-link'>Payment Audit</NavLink>
                        <NavLink to="/sent-invoices" className='nav-link'>Invoices</NavLink>
                        {isSuperAdmin && (
                            <>
                                <NavLink to="/other-income" className='nav-link'>Other Income</NavLink>
                                <NavLink to="/projected-income" className='nav-link'>Projected Income</NavLink>
                                <NavLink to="/balance-sheet" className='nav-link'>Balance Sheet</NavLink>
                                <NavLink to="/fee-balances" className='nav-link'>Fee Balances</NavLink>
                                <NavLink to="/marketing-budget" className='nav-link'>Marketing Budgets</NavLink>
                                <NavLink to="/vendors" className='nav-link'>Vendors</NavLink>
                                <NavLink to="/fixed-bills" className='nav-link'>Fixed Bills</NavLink>
                                <NavLink to="/pay-bills" className='nav-link'>Pay Bills</NavLink>
                                <NavLink to="/direct-expenses" className='nav-link'>Direct Expenses</NavLink>
                                <NavLink to="/requisitions" className='nav-link'>Make Requisition</NavLink>
                                <NavLink to="/requisition-approvals" className='nav-link'>Requisition Approvals</NavLink>
                                <NavLink to="/statutory-remittances" className='nav-link'>Statutory Remittances</NavLink>
                                <NavLink to="/marketing-expenses" className='nav-link'>Marketing Expenses</NavLink>
                                <NavLink to="/debts-loans" className='nav-link'>Liabilities</NavLink>
                                <NavLink to="/assets" className='nav-link'>Assets</NavLink>
                                <NavLink to="/finance-reports" className='nav-link'>Reports</NavLink>
                            </>
                        )}
                    </div>
                )}
            </div>
            {isSuperAdmin && (
                <>
                    <div className='nav-item '>
                        <div className='d-flex align-items-center leftNavLinks leftLinkCont dropdownMyCont' onClick={toggleCollapse3} style={{ cursor: 'pointer' }}>
                            <div>
                                <FontAwesomeIcon icon={faUserTie} className="laftNavIcon" />
                                <span>HR</span>
                            </div>
                            <FontAwesomeIcon icon={isCollapsed3 ? faChevronDown : faChevronUp} className="ml-auto leftLinkNotif2" />
                        </div>
                        {!isCollapsed3 && (
                            <div className='submenu'>
                                <NavLink to="/employees" className='nav-link'>Employees</NavLink>
                                <NavLink to="/leave-request" className='nav-link'>Leave Request</NavLink>
                                <NavLink to="/payroll-ledger" className='nav-link'>Payroll Ledger</NavLink>
                                <NavLink to="/pay-salaries" className='nav-link'>Pay Salaries</NavLink>
                                <NavLink to="/cash-salary-payments" className='nav-link'>Cash Salary Payments</NavLink>
                                <NavLink to="/directors" className='nav-link'>Directors</NavLink>
                                <NavLink to="/shareholders" className='nav-link'>Shareholders</NavLink>
                                <NavLink to="/admins" className='nav-link'>Admins</NavLink>
                    </div>
                )}
            </div>
                </>
            )}

            <p>ACADEMIC</p>
            {isSuperAdmin && (
                <NavLink to="/courses" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                    <FontAwesomeIcon icon={faCode} className="laftNavIcon" />
                    Courses
                </NavLink>
            )}
            <NavLink to="/intakes" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faCalendarDay} className="laftNavIcon" />
                Intakes
            </NavLink>
            <NavLink to="/darasa" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faChalkboardTeacher} className="laftNavIcon" />
                Classes
            </NavLink>
            {isSuperAdmin && (
                <>
                    <NavLink to="/branches" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faBuilding} className="laftNavIcon" />
                        Campuses
                    </NavLink>

                    <p>LAB</p>
                    <NavLink to="/lab-onboarding" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faLaptopCode} className="laftNavIcon" />
                        Onboarding
                        <span
                            className={`leftLinkNotif ${unopenedLabCount > 0 ? 'leftLinkNotif--fresh' : ''}`}
                            title={`${unopenedLabCount} unread onboarding requests`}
                        >
                            {unopenedLabCount > 99 ? '99+' : unopenedLabCount}
                        </span>
                    </NavLink>
                    <div className='nav-item '>
                        <div className='d-flex align-items-center leftNavLinks leftLinkCont dropdownMyCont' onClick={toggleCollapse7} style={{ cursor: 'pointer' }}>
                            <div>
                                <FontAwesomeIcon icon={faUserFriends} className="laftNavIcon" />
                                <span>Clients</span>
                            </div>
                            <FontAwesomeIcon icon={isCollapsed7 ? faChevronDown : faChevronUp} className="ml-auto leftLinkNotif2" />
                        </div>
                        {!isCollapsed7 && (
                            <div className='submenu'>
                                <NavLink to="/managed-products" className='nav-link'>Managed Product</NavLink>
                                <NavLink to="/managed-service" className='nav-link'>Managed Service</NavLink>
                                <NavLink to="/extended-teams" className='nav-link'>Extended Team</NavLink>
                            </div>
                        )}
                    </div>
                    <NavLink to="/devs" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faSitemap} className="laftNavIcon" />
                        Devs
                    </NavLink>
                    <NavLink to="/lab-emails" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faEnvelope} className="laftNavIcon" />
                        Lab Emails
                    </NavLink>
                </>
            )}

            {isSuperAdmin && (
                <>
                    <p>MARKETING</p>
                    <NavLink to="/enrolled-partners" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faUserFriends} className="laftNavIcon" />
                        Enrolled partners
                    </NavLink>
                    <NavLink to="/jobs-scout" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faBriefcase} className="laftNavIcon" />
                        Jobs Scout
                    </NavLink>
                    <NavLink to="/growth-opportunity-scout" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faHandshake} className="laftNavIcon" />
                        Opportunity Scout
                    </NavLink>
                    <NavLink to="/news-posts" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faNewspaper} className="laftNavIcon" />
                        News Posts
                    </NavLink>
                </>
            )}

            {isAdmin && (
                <>
                    <p>STRATEGY</p>
                    <NavLink to="/strategic-roadmap" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faCompass} className="laftNavIcon" />
                        Strategic Roadmap
                    </NavLink>
                </>
            )}

        </div>
    );
}

export default LeftBar;
